import React from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

/**
 * "Covers 28 of 36 months" meter with the list of gaps, fed by computeCoverage().
 */
const CoverageMeter = ({ coverage, label, gapLabel = 'gap', explainThreshold = 1, className = '' }) => {
  if (!coverage) return null;
  const percent = Math.round(coverage.coverageRatio * 100);
  const complete = coverage.isFullyCovered;
  const bigGaps = coverage.gaps.filter((gap) => gap.months > explainThreshold);
  const years = coverage.requiredMonths / 12;

  return (
    <div className={`rounded-lg border p-3 ${complete ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'} ${className}`} aria-live="polite">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-gray-900">
          {label || `Last ${years} year${years === 1 ? '' : 's'}`}: covers {coverage.coveredMonths} of {coverage.requiredMonths} months
        </p>
        {complete
          ? <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600" aria-label="Complete" />
          : <span className="text-xs text-gray-500">{coverage.requiredStart} to {coverage.requiredEnd}</span>}
      </div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100} aria-label={`${label} coverage`}>
        <div className={`h-full rounded-full transition-all ${complete ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${percent}%` }} />
      </div>
      {!complete && coverage.gaps.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-gray-700">
          {coverage.gaps.map((gap) => (
            <li key={`${gap.from}-${gap.to}`} className="flex items-start gap-1.5">
              {gap.months > explainThreshold
                ? <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-500" aria-hidden="true" />
                : <span className="mt-0.5 inline-block h-3.5 w-3.5 flex-shrink-0 text-center text-gray-500" aria-hidden="true">·</span>}
              <span>
                {gap.months === 1 ? '1 month' : `${gap.months} months`} not covered: {gap.from === gap.to ? gap.from : `${gap.from} to ${gap.to}`}
                {gap.months > explainThreshold && bigGaps.length > 0 ? ` (${gapLabel} needs attention)` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CoverageMeter;
